// @ts-nocheck
/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { createTheme } from "@mui/material/styles";

// Soft UI Dashboard React base styles
import colors from "assets/theme/base/colors";
import breakpoints from "assets/theme/base/breakpoints";
import typography from "assets/theme/base/typography";
import boxShadows from "assets/theme/base/boxShadows";
import borders from "assets/theme/base/borders";
import globals from "assets/theme/base/globals";

// Soft UI Dashboard React helper functions
import boxShadow from "assets/theme/functions/boxShadow";
import hexToRgb from "assets/theme/functions/hexToRgb";
import linearGradient from "assets/theme/functions/linearGradient";
import pxToRem from "assets/theme/functions/pxToRem";
import rgba from "assets/theme/functions/rgba";

// Soft UI Dashboard React components base styles for @mui material components
import sidenav from "assets/theme/components/sidenav";
import list from "assets/theme/components/list";
import listItem from "assets/theme/components/list/listItem";
import listItemText from "assets/theme/components/list/listItemText";
import card from "assets/theme/components/card";
import cardMedia from "assets/theme/components/card/cardMedia";
import cardContent from "assets/theme/components/card/cardContent";
import button from "assets/theme/components/button";
import iconButton from "assets/theme/components/iconButton";
import inputBase from "assets/theme/components/form/inputBase";
import menu from "assets/theme/components/menu";
import menuItem from "assets/theme/components/menu/menuItem";
import switchButton from "assets/theme/components/form/switchButton";
import divider from "assets/theme/components/divider";
import tableContainer from "assets/theme/components/table/tableContainer";
import tableHead from "assets/theme/components/table/tableHead";
import tableCell from "assets/theme/components/table/tableCell";
import linearProgress from "assets/theme/components/linearProgress";
import breadcrumbs from "assets/theme/components/breadcrumbs";
import slider from "assets/theme/components/slider";
import avatar from "assets/theme/components/avatar";
import tooltip from "assets/theme/components/tooltip";
import appBar from "assets/theme/components/appBar";
import tabs from "assets/theme/components/tabs";
import tab from "assets/theme/components/tabs/tab";
import stepper from "assets/theme/components/stepper";
import step from "assets/theme/components/stepper/step";
import stepConnector from "assets/theme/components/stepper/stepConnector";
import stepLabel from "assets/theme/components/stepper/stepLabel";
import stepIcon from "assets/theme/components/stepper/stepIcon";
import select from "assets/theme/components/form/select";
import formControlLabel from "assets/theme/components/form/formControlLabel";
import formLabel from "assets/theme/components/form/formLabel";
import checkbox from "assets/theme/components/form/checkbox";
import radio from "assets/theme/components/form/radio";
import autocomplete from "assets/theme/components/form/autocomplete";
import input from "assets/theme/components/form/input";
import container from "assets/theme/components/container";
import popover from "assets/theme/components/popover";
import buttonBase from "assets/theme/components/buttonBase";
import icon from "assets/theme/components/icon";
import svgIcon from "assets/theme/components/svgIcon";
import link from "assets/theme/components/link";

const themeFunctions = {
  boxShadow,
  hexToRgb,
  linearGradient,
  pxToRem,
  rgba,
};

function getPaletteForMode(colorMode) {
  if (colorMode !== "dark") {
    return { ...colors };
  }

  return {
    ...colors,
    background: {
      default: "#0f1419",
      paper: "#1a2030",
    },
    text: {
      main: "#94a3b8",
      focus: "#cbd5e1",
    },
    white: {
      main: "#1e293b",
      focus: "#253045",
    },
    light: {
      main: "#334155",
      focus: "#475569",
    },
    dark: {
      main: "#e2e8f0",
      focus: "#f1f5f9",
    },
    grey: {
      100: "#1e293b",
      200: "#253045",
      300: "#334155",
      400: "#475569",
      500: "#64748b",
      600: "#94a3b8",
      700: "#cbd5e1",
      800: "#e2e8f0",
      900: "#f1f5f9",
    },
    inputColors: {
      borderColor: { main: "#475569", focus: "#35d1f5" },
      boxShadow: "#1e3a5f",
      error: "#fd5c70",
      success: "#66d432",
    },
    gradients: {
      ...colors.gradients,
      dark: {
        main: "#94a3b8",
        state: "#cbd5e1",
      },
    },
  };
}

function getThemeComponents(palette, isDark) {
  const baseComponents = {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...container,
        ...(isDark
          ? {
              body: {
                backgroundColor: palette.background.default,
                color: palette.text.main,
              },
            }
          : {}),
      },
    },
    MuiDrawer: isDark
      ? {
          ...sidenav,
          styleOverrides: {
            ...sidenav.styleOverrides,
            paper: {
              ...sidenav.styleOverrides.paper,
              backgroundColor: rgba(palette.white.main, 0.95),
            },
          },
        }
      : { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: isDark
      ? {
          ...card,
          styleOverrides: {
            ...card.styleOverrides,
            root: {
              ...card.styleOverrides.root,
              backgroundColor: palette.white.main,
              border: `${borders.borderWidth[0]} solid ${rgba(palette.black.main, 0.12)}`,
            },
          },
        }
      : { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInputBase: { ...inputBase },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiDivider: { ...divider },
    MuiTableContainer: isDark
      ? {
          ...tableContainer,
          styleOverrides: {
            ...tableContainer.styleOverrides,
            root: {
              ...tableContainer.styleOverrides.root,
              backgroundColor: palette.white.main,
            },
          },
        }
      : { ...tableContainer },
    MuiTableHead: isDark
      ? {
          ...tableHead,
          styleOverrides: {
            ...tableHead.styleOverrides,
            root: {
              backgroundColor: palette.grey[200],
            },
          },
        }
      : { ...tableHead },
    MuiTableCell: isDark
      ? {
          ...tableCell,
          styleOverrides: {
            ...tableCell.styleOverrides,
            root: {
              borderBottomColor: palette.grey[300],
              color: palette.text.main,
            },
          },
        }
      : { ...tableCell },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiAvatar: { ...avatar },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiStepper: { ...stepper },
    MuiStep: { ...step },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiStepIcon: { ...stepIcon },
    MuiSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    MuiCheckbox: { ...checkbox },
    MuiRadio: { ...radio },
    MuiAutocomplete: { ...autocomplete },
    MuiInput: { ...input },
    MuiOutlinedInput: { ...input },
    MuiFilledInput: { ...input },
    MuiPopover: { ...popover },
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
    ...(isDark
      ? {
          MuiDialog: {
            styleOverrides: {
              paper: {
                backgroundColor: palette.white.main,
                color: palette.dark.main,
                backgroundImage: "none",
              },
            },
          },
          MuiDialogTitle: {
            styleOverrides: {
              root: {
                color: palette.dark.main,
              },
            },
          },
          MuiDialogContent: {
            styleOverrides: {
              root: {
                color: palette.text.main,
              },
            },
          },
          MuiDialogActions: {
            styleOverrides: {
              root: {
                borderTop: `1px solid ${rgba(palette.dark.main, 0.12)}`,
              },
            },
          },
        }
      : {}),
  };

  return baseComponents;
}

export function createAppTheme(colorMode = "light", extraOptions = {}) {
  const isDark = colorMode === "dark";
  const palette = getPaletteForMode(colorMode);

  return createTheme({
    ...extraOptions,
    breakpoints: { ...breakpoints },
    palette: {
      mode: isDark ? "dark" : "light",
      ...palette,
    },
    typography: { ...typography },
    boxShadows: { ...boxShadows },
    borders: { ...borders },
    functions: themeFunctions,
    components: getThemeComponents(palette, isDark),
  });
}

export default createAppTheme("light");
